import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CultureIndexCulture } from 'types/Meteorology/Agro/CultureIndex/culture';
import {
  CultureIndexAreaMap,
  CultureIndexAreaMapError,
  emptyMapParams,
} from 'types/Meteorology/Agro/CultureIndex/Area/map';
import { CultureIndexPhenologicalStage } from 'types/Meteorology/Agro/CultureIndex/phenologicalStage';
import { CultureIndexMapData } from 'types/Meteorology/Agro/CultureIndex/Area/getMapData';

interface MeteorologyAgroCultureIndexAreaState {
  maps: CultureIndexAreaMap[];
}

const initialState: MeteorologyAgroCultureIndexAreaState = {
  maps: [{
    id: 0,
    data: undefined,
    error: undefined,
    filename: '',
    params: { ...emptyMapParams },
    isLoading: false,
  }],
};

export const meteorologyAgroCultureIndexAreaSlice = createSlice({
  name: 'meteorologyAgroCultureIndexArea',
  initialState,
  reducers: {
    resetMapParams: (state) => {
      state.maps.forEach((map) => {
        map.params = { ...emptyMapParams };
      });
    },
    reset: (state) => {
      // Restaura o estado inicial completo: apenas um mapa com todos os valores limpos
      state.maps = [{
        id: 0,
        data: undefined,
        error: undefined,
        filename: '',
        params: { ...emptyMapParams },
        isLoading: false,
      }];
    },
    setMaps: (state, action: PayloadAction<CultureIndexAreaMap[]>) => {
      state.maps = action.payload;
    },
    setCulture: (
      state,
      action: PayloadAction<{ id: number; culture: CultureIndexCulture | undefined }>,
    ) => {
      const { id, culture } = action.payload;
      const map = state.maps.find((m) => m.id === id);
      if (map) {
        map.params.culture = culture;
      }
    },
    setForecastDate: (
      state,
      action: PayloadAction<{ id: number; forecastDate: string | undefined }>,
    ) => {
      const { id, forecastDate } = action.payload;
      const map = state.maps.find((m) => m.id === id);
      if (map) {
        map.params.forecastDate = forecastDate;
      }
    },
    setPeriodStart: (
      state,
      action: PayloadAction<{ id: number; periodStart: string | undefined }>,
    ) => {
      const { id, periodStart } = action.payload;
      const map = state.maps.find((m) => m.id === id);
      if (map) {
        map.params.periodStart = periodStart;
      }
    },
    setPeriodEnd: (
      state,
      action: PayloadAction<{ id: number; periodEnd: string | undefined }>,
    ) => {
      const { id, periodEnd } = action.payload;
      const map = state.maps.find((m) => m.id === id);
      if (map) {
        map.params.periodEnd = periodEnd;
      }
    },
    setStage: (
      state,
      action: PayloadAction<{ id: number; stage: CultureIndexPhenologicalStage | undefined }>,
    ) => {
      const { id, stage } = action.payload;
      const map = state.maps.find((m) => m.id === id);
      if (map) {
        map.params.stage = stage;
      }
    },
    setFilename: (
      state,
      action: PayloadAction<{ id: number; filename: string }>,
    ) => {
      const { id, filename } = action.payload;
      const map = state.maps.find((m) => m.id === id);
      if (map) {
        map.filename = filename;
      }
    },
    setData: (
      state,
      action: PayloadAction<{
        id: number;
        data?: CultureIndexMapData;
        error?: CultureIndexAreaMapError;
      }>,
    ) => {
      const { id, data, error } = action.payload;
      const map = state.maps.find((m) => m.id === id);
      if (map) {
        map.data = data;
        map.error = error;
        map.isLoading = false;
      }
    },
    setLoading: (
      state,
      action: PayloadAction<{ id: number; isLoading: boolean }>,
    ) => {
      const { id, isLoading } = action.payload;
      const map = state.maps.find((m) => m.id === id);
      if (map) {
        map.isLoading = isLoading;
      }
    },
  },
});

export const selectMeteorologyAgroCultureIndexAreaMaps = (state: any) => (
  state.meteorologyAgroCultureIndexArea.maps
);

export const {
  resetMapParams,
  reset,
  setMaps,
  setCulture,
  setForecastDate,
  setPeriodStart,
  setPeriodEnd,
  setStage,
  setFilename,
  setData,
  setLoading,
} = meteorologyAgroCultureIndexAreaSlice.actions;

export default meteorologyAgroCultureIndexAreaSlice.reducer;
